import { FC, useState, useEffect } from 'react'
import { SellerConfigItem } from '../types'
import { getSellerConfig, createSellerConfig, updateSellerConfig, deleteSellerConfig } from '../services'
import Swal from 'sweetalert2'

interface ConfigTabProps {
  pluginspaceId: number
}

export const ConfigTab: FC<ConfigTabProps> = ({ pluginspaceId }) => {
  const [config, setConfig] = useState<SellerConfigItem>({
    pluginspaceId,
    sendMailQueue: false,
    sendPush: false,
    sendWhatsapp: false,
    sendSMS: false,
    linkPainel: null,
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasExistingConfig, setHasExistingConfig] = useState(false)

  // Carregar configuração ao montar o componente
  useEffect(() => {
    loadConfig()
  }, [pluginspaceId])

  const loadConfig = async () => {
    try {
      setLoading(true)
      console.log('[ConfigTab] Loading config...')
      const existingConfig = await getSellerConfig()
      
      if (existingConfig) {
        console.log('[ConfigTab] Found existing config:', existingConfig)
        setConfig(existingConfig)
        setHasExistingConfig(true)
      } else {
        console.log('[ConfigTab] No existing config found, using defaults')
        setConfig({
          pluginspaceId,
          sendMailQueue: false,
          sendPush: false,
          sendWhatsapp: false,
          sendSMS: false,
          linkPainel: null,
        })
        setHasExistingConfig(false)
      }
    } catch (error) {
      console.error('[ConfigTab] Error loading config:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar configurações',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCheckboxChange = (field: keyof Pick<SellerConfigItem, 'sendMailQueue' | 'sendPush' | 'sendWhatsapp' | 'sendSMS'>) => {
    setConfig((prev) => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig((prev) => ({
      ...prev,
      linkPainel: e.target.value || null,
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Validação simples
      if (config.linkPainel && !isValidUrl(config.linkPainel)) {
        Swal.fire({
          icon: 'warning',
          title: 'URL inválida',
          text: 'Por favor, insira uma URL válida para o painel',
          confirmButtonText: 'OK',
        })
        setSaving(false)
        return
      }

      // Prepara os dados para enviar (sem id, created, updated)
      const dataToSave = {
        pluginspaceId: config.pluginspaceId,
        sendMailQueue: config.sendMailQueue,
        sendPush: config.sendPush,
        sendWhatsapp: config.sendWhatsapp,
        sendSMS: config.sendSMS,
        linkPainel: config.linkPainel,
      }

      console.log('[ConfigTab] Saving config:', dataToSave)

      let result
      if (hasExistingConfig) {
        console.log('[ConfigTab] Updating existing config...')
        result = await updateSellerConfig(dataToSave)
      } else {
        console.log('[ConfigTab] Creating new config...')
        result = await createSellerConfig(dataToSave)
        setHasExistingConfig(true)
      }

      console.log('[ConfigTab] Save success, result:', result)
      
      // Atualiza o estado com resposta do servidor
      if (result) {
        setConfig(result)
      }

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Configurações salvas com sucesso!',
        confirmButtonText: 'OK',
      })
    } catch (error) {
      console.error('[ConfigTab] Error saving config:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao salvar',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!hasExistingConfig) {
      Swal.fire({
        icon: 'info',
        title: 'Nenhuma configuração',
        text: 'Não há configurações para deletar',
        confirmButtonText: 'OK',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Confirmar exclusão',
      text: 'Tem certeza que deseja deletar todas as configurações?',
      showCancelButton: true,
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    })

    if (result.isConfirmed) {
      try {
        setSaving(true)
        console.log('[ConfigTab] Deleting config...')
        await deleteSellerConfig()

        // Resetar formulário
        setConfig({
          pluginspaceId,
          sendMailQueue: false,
          sendPush: false,
          sendWhatsapp: false,
          sendSMS: false,
          linkPainel: null,
        })
        setHasExistingConfig(false)

        Swal.fire({
          icon: 'success',
          title: 'Deletado!',
          text: 'Configurações deletadas com sucesso!',
          confirmButtonText: 'OK',
        })
      } catch (error) {
        console.error('[ConfigTab] Error deleting config:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro ao deletar',
          text: error instanceof Error ? error.message : 'Erro desconhecido',
          confirmButtonText: 'OK',
        })
      } finally {
        setSaving(false)
      }
    }
  }

  const isValidUrl = (urlString: string): boolean => {
    try {
      new URL(urlString)
      return true
    } catch {
      return false
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='container-fluid mt-4'>
      <div className='row'>
        <div className='col-md-8'>
          <div className='card'>
            <div className='card-header'>
              <h3 className='card-title'>Configurações de Notificação</h3>
            </div>
            <div className='card-body'>
              {/* Checkbox: Email */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendMailQueue'
                    checked={config.sendMailQueue}
                    onChange={() => handleCheckboxChange('sendMailQueue')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendMailQueue'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar email para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores por email sobre mudanças de posição na fila
                    </p>
                  </label>
                </div>
              </div>

              {/* Checkbox: Push */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendPush'
                    checked={config.sendPush}
                    onChange={() => handleCheckboxChange('sendPush')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendPush'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar Push para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores através de push notification sobre mudanças de posição
                    </p>
                  </label>
                </div>
              </div>

              {/* Checkbox: WhatsApp */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendWhatsapp'
                    checked={config.sendWhatsapp}
                    onChange={() => handleCheckboxChange('sendWhatsapp')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendWhatsapp'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar WhatsApp para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores via WhatsApp sobre mudanças de posição na fila
                    </p>
                  </label>
                </div>
              </div>

              {/* Checkbox: SMS */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendSMS'
                    checked={config.sendSMS}
                    onChange={() => handleCheckboxChange('sendSMS')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendSMS'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar SMS para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores via SMS sobre mudanças de posição na fila
                    </p>
                  </label>
                </div>
              </div>

              {/* Input: Link do Painel */}
              <div className='mb-4'>
                <label htmlFor='linkPainel' className='form-label' style={{ fontSize: '16px', fontWeight: '500' }}>
                  Link do Painel de Fila
                </label>
                <input
                  type='url'
                  className='form-control'
                  id='linkPainel'
                  placeholder='https://exemplo.com/painel-fila'
                  value={config.linkPainel || ''}
                  onChange={handleLinkChange}
                  disabled={saving}
                  style={{ fontSize: '14px' }}
                />
                <small className='form-text text-muted d-block mt-2'>
                  URL para o painel de acompanhamento da fila que será compartilhada com os vendedores
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna de Ações */}
        <div className='col-md-4'>
          <div className='card'>
            <div className='card-header'>
              <h3 className='card-title'>Ações</h3>
            </div>
            <div className='card-body d-flex flex-column gap-3'>
              <button
                className='btn btn-primary'
                onClick={handleSave}
                disabled={saving}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                {saving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className='fas fa-save me-2'></i>
                    Salvar Configurações
                  </>
                )}
              </button>

              <button
                className='btn btn-secondary'
                onClick={loadConfig}
                disabled={saving || loading}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                <i className='fas fa-sync-alt me-2'></i>
                Recarregar
              </button>

              <button
                className='btn btn-danger'
                onClick={handleDelete}
                disabled={saving || loading || !hasExistingConfig}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                <i className='fas fa-trash me-2'></i>
                Deletar Tudo
              </button>

              <div className='alert alert-info mt-3' role='alert'>
                <i className='fas fa-info-circle me-2'></i>
                <strong>Status:</strong> <br />
                {hasExistingConfig ? (
                  <span className='text-success'>Configurações ativas</span>
                ) : (
                  <span className='text-warning'>Nenhuma configuração salva</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
